import { useMemo } from 'react'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import 'katex/contrib/mhchem/mhchem'
import type { QuestionResponse, QuestionResponseDetailResponse } from '../types'

interface ReviewQuestionViewerProps {
  question: QuestionResponse
  response: QuestionResponseDetailResponse
  questionNumber: number
  totalQuestions: number
  showSolution: boolean
}

const OPTION_LABELS = ['A', 'B', 'C', 'D']
const IMAGE_BASE_URL = 'https://api.myneetbuddy.com/mnb-qb-images'

function renderLatexContent(htmlContent: string): string {
  if (!htmlContent) return ''

  let result = htmlContent

  result = result.replace(/\$\$([\s\S]*?)\$\$/g, (_, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: true,
        throwOnError: false,
        trust: true,
        strict: false,
      })
    } catch {
      return `<span class="text-red-500">[Math Error: ${latex}]</span>`
    }
  })

  result = result.replace(/\$([^$\n]+?)\$/g, (_, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: false,
        throwOnError: false,
        trust: true,
        strict: false,
      })
    } catch {
      return `<span class="text-red-500">[Math Error: ${latex}]</span>`
    }
  })

  result = result.replace(/\\\(([^)]+?)\\\)/g, (_, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: false,
        throwOnError: false,
        trust: true,
        strict: false,
      })
    } catch {
      return `<span class="text-red-500">[Math Error: ${latex}]</span>`
    }
  })

  result = result.replace(/\\\[([\s\S]*?)\\\]/g, (_, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: true,
        throwOnError: false,
        trust: true,
        strict: false,
      })
    } catch {
      return `<span class="text-red-500">[Math Error: ${latex}]</span>`
    }
  })

  return result
}

function processImagePlaceholders(content: string, testCode?: string): string {
  if (!content || !testCode) return content

  return content.replace(/#img\[([^\]]+)\]/g, (_, imageName) => {
    const imageUrl = `${IMAGE_BASE_URL}/${testCode}/${imageName}`
    return `<img src="${imageUrl}" alt="${imageName}" class="max-w-full h-auto my-2 rounded-lg" />`
  })
}

function formatTimeSpent(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  if (totalSeconds < 60) return `${totalSeconds}s`
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`
}

function getStatusPill(response: QuestionResponseDetailResponse) {
  switch (response.status) {
    case 'ATTEMPTED':
      return { label: 'Attempted', className: 'bg-blue-100 text-blue-700' }
    case 'SKIPPED':
      return { label: 'Skipped', className: 'bg-yellow-100 text-yellow-700' }
    case 'UNSEEN':
      return { label: 'Unseen', className: 'bg-gray-100 text-gray-600' }
    case 'SEEN':
      return { label: 'Seen', className: 'bg-gray-100 text-gray-600' }
    default:
      return { label: response.status, className: 'bg-gray-100 text-gray-600' }
  }
}

function getResultBadge(response: QuestionResponseDetailResponse) {
  if (!response.isAttempted) return null
  if (response.isCorrect) {
    return { label: 'Correct', className: 'bg-green-100 text-green-700' }
  }
  return { label: 'Incorrect', className: 'bg-red-100 text-red-700' }
}

function getMarksDisplay(marks?: number) {
  if (marks === undefined || marks === null) return { text: '0', className: 'text-gray-600' }
  if (marks > 0) return { text: `+${marks}`, className: 'text-green-600' }
  if (marks < 0) return { text: `${marks}`, className: 'text-red-600' }
  return { text: '0', className: 'text-gray-600' }
}

export default function ReviewQuestionViewer({
  question,
  response,
  questionNumber,
  totalQuestions,
  showSolution,
}: ReviewQuestionViewerProps) {
  const testCode = question.testCode
  const correctAnswer = question.solutionData?.answer
  const studentAnswer = response.finalAnswerOption

  const renderedQuestion = useMemo(
    () => renderLatexContent(processImagePlaceholders(question.questionData.text, testCode)),
    [question.questionData.text, testCode]
  )

  const renderedOptions = useMemo(
    () =>
      question.questionData.options.map((opt) =>
        renderLatexContent(processImagePlaceholders(opt, testCode))
      ),
    [question.questionData.options, testCode]
  )

  const renderedExplanation = useMemo(
    () =>
      question.solutionData?.explanation
        ? renderLatexContent(processImagePlaceholders(question.solutionData.explanation, testCode))
        : null,
    [question.solutionData?.explanation, testCode]
  )

  const statusPill = getStatusPill(response)
  const resultBadge = getResultBadge(response)
  const marksDisplay = getMarksDisplay(response.marksAwarded)

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {/* Question Info Bar */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600">
            Question {questionNumber} of {totalQuestions}
          </span>
          <div className="flex gap-2">
            <span
              className={`px-2 py-1 text-xs rounded-full ${
                question.difficultyLevel === 'EASY'
                  ? 'bg-green-100 text-green-700'
                  : question.difficultyLevel === 'MEDIUM'
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {question.difficultyLevel}
            </span>
            {response.subjectCode && (
              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                {response.subjectCode}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Response Metadata Bar */}
      <div className="flex-shrink-0 px-4 py-2 border-b border-gray-100 bg-white">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={`px-2 py-0.5 rounded-full font-medium ${statusPill.className}`}>
            {statusPill.label}
          </span>
          {resultBadge && (
            <span className={`px-2 py-0.5 rounded-full font-medium ${resultBadge.className}`}>
              {resultBadge.label}
            </span>
          )}
          <span className={`font-semibold ${marksDisplay.className}`}>
            {marksDisplay.text} marks
          </span>
          <span className="text-gray-400">|</span>
          <span className="text-gray-600">{formatTimeSpent(response.totalTimeMs)}</span>
          {response.answerChangeCount > 0 && (
            <>
              <span className="text-gray-400">|</span>
              <span className="text-orange-600">{response.answerChangeCount} change{response.answerChangeCount > 1 ? 's' : ''}</span>
            </>
          )}
          {response.guessedFlag && (
            <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Guessed</span>
          )}
          {response.lowConfidenceFlag && (
            <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">Low Confidence</span>
          )}
          {response.hesitationFlag && (
            <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Hesitation</span>
          )}
        </div>
      </div>

      {/* Question Content - Scrollable */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {/* Question Text */}
        <div
          className="text-gray-800 text-base leading-relaxed mb-6"
          dangerouslySetInnerHTML={{ __html: renderedQuestion }}
        />

        {/* Options with triple-state highlighting */}
        <div className="space-y-3">
          {renderedOptions.map((optionHtml, idx) => {
            const optionLabel = OPTION_LABELS[idx]
            const isCorrectOption = showSolution && correctAnswer === optionLabel
            const isStudentWrongAnswer =
              showSolution &&
              studentAnswer === optionLabel &&
              studentAnswer !== correctAnswer
            const isStudentCorrectAnswer =
              showSolution &&
              studentAnswer === optionLabel &&
              studentAnswer === correctAnswer

            let borderClass = 'border-gray-200'
            let bgClass = ''
            let labelBg = 'bg-gray-100 text-gray-600'

            if (isStudentWrongAnswer) {
              borderClass = 'border-red-500'
              bgClass = 'bg-red-50'
              labelBg = 'bg-red-500 text-white'
            } else if (isCorrectOption) {
              borderClass = 'border-green-500'
              bgClass = 'bg-green-50'
              labelBg = 'bg-green-500 text-white'
            }

            if (isStudentCorrectAnswer) {
              labelBg = 'bg-green-500 text-white'
            }

            return (
              <div
                key={idx}
                className={`flex items-start p-3 rounded-lg border transition-colors ${borderClass} ${bgClass}`}
              >
                <span
                  className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium mr-3 ${labelBg}`}
                >
                  {optionLabel}
                </span>
                <div
                  className="flex-1 text-gray-700"
                  dangerouslySetInnerHTML={{ __html: optionHtml }}
                />
                {isStudentWrongAnswer && (
                  <span className="flex-shrink-0 ml-2 text-xs text-red-600 font-medium">Your answer</span>
                )}
                {isStudentCorrectAnswer && (
                  <span className="flex-shrink-0 ml-2 text-xs text-green-600 font-medium">Your answer</span>
                )}
              </div>
            )
          })}
        </div>

        {/* Solution Section */}
        {showSolution && question.solutionData && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Solution</h3>

            <div className="mb-4">
              <span className="text-sm font-medium text-gray-600">Correct Answer: </span>
              <span className="text-green-600 font-bold">{correctAnswer}</span>
            </div>

            {renderedExplanation && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Explanation:</h4>
                <div
                  className="text-gray-700 leading-relaxed bg-blue-50 p-4 rounded-lg"
                  dangerouslySetInnerHTML={{ __html: renderedExplanation }}
                />
              </div>
            )}

            {question.solutionData.steps && question.solutionData.steps.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Solution:</h4>
                <div className="space-y-3">
                  {question.solutionData.steps.map((step, idx) => (
                    <p
                      key={idx}
                      className="text-gray-700 leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: renderLatexContent(processImagePlaceholders(step, testCode)),
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {question.solutionData.keyConcepts &&
              question.solutionData.keyConcepts.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-2">Key Concepts:</h4>
                  <div className="flex flex-wrap gap-2">
                    {question.solutionData.keyConcepts.map((concept, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full"
                      >
                        {concept}
                      </span>
                    ))}
                  </div>
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  )
}
